import Phaser from 'phaser';
import {
  CHARACTER_TEXTURES,
  ENVIRONMENT_TEXTURES,
  TEXTURE_KEYS,
  UI_TEXTURES,
} from '../assets/assetKeys';
import { ANIM_KEYS } from '../assets/assetKeys';
import { GAME_HEIGHT, GAME_WIDTH, SCENE_KEYS } from '../data/gameConstants';
import { Button } from '../ui/Button';
import { UIStyle } from '../ui/UIStyle';
import { SceneTransition } from '../utils/SceneTransition';

export class AssetPreviewScene extends Phaser.Scene {
  private scrollY = 0;
  private contentHeight = 0;
  private contentContainer?: Phaser.GameObjects.Container;

  constructor() {
    super({ key: 'AssetPreviewScene' });
  }

  create(): void {
    this.cameras.main.setBackgroundColor(0x1a1a2e);
    this.scrollY = 0;

    this.add.text(GAME_WIDTH / 2, 30, 'Asset Preview — SEISM', {
      ...UIStyle.textStyle('28px', '#d4a373', true),
    }).setOrigin(0.5).setScrollFactor(0).setDepth(100);

    this.contentContainer = this.add.container(0, 0);
    let y = 70;

    y = this.addSection('Karakter', CHARACTER_TEXTURES, y, 140);
    y = this.addPlayerAnimationPreview(y + 20);
    y = this.addSection('Environment & Background', ENVIRONMENT_TEXTURES, y, 160);
    y = this.addSection('UI Elements', UI_TEXTURES, y, 80);
    y = this.addUISample(y + 30);

    this.contentHeight = y + 100;

    this.input.on('wheel', (_pointer: Phaser.Input.Pointer, _gos: unknown, _dx: number, dy: number) => {
      this.scrollY = Phaser.Math.Clamp(this.scrollY - dy * 0.5, 0, Math.max(0, this.contentHeight - GAME_HEIGHT + 80));
      this.contentContainer?.setY(-this.scrollY);
    });

    let dragStart = 0;
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      dragStart = pointer.y + this.scrollY;
    });
    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (!pointer.isDown) return;
      this.scrollY = Phaser.Math.Clamp(dragStart - pointer.y, 0, Math.max(0, this.contentHeight - GAME_HEIGHT + 80));
      this.contentContainer?.setY(-this.scrollY);
    });

    new Button(this, {
      x: GAME_WIDTH - 140,
      y: GAME_HEIGHT - 36,
      text: 'Kembali ke Menu',
      width: 240,
      fontSize: '16px',
      onClick: () => SceneTransition.startNextFrame(this, SCENE_KEYS.MAIN_MENU),
    });
  }

  private addSection(title: string, keys: string[], startY: number, cellH: number): number {
    const container = this.contentContainer!;
    const titleText = this.add.text(40, startY, title, {
      ...UIStyle.textStyle('22px', '#52b788', true),
    });
    container.add(titleText);

    let x = 60;
    let y = startY + 36;
    const maxX = GAME_WIDTH - 60;

    keys.forEach((key) => {
      if (!this.textures.exists(key)) return;

      const cell = this.add.container(x, y);
      const frame = this.textures.getFrame(key);
      const scale = Math.min(1, 100 / Math.max(frame.width, frame.height));
      const img = this.add.image(0, 0, key);
      img.setScale(scale);

      const label = this.add.text(0, cellH * 0.38, key.replace(/^(player_|npc_|env_|bg_|ui_)/, ''), {
        ...UIStyle.textStyle('11px', '#aaaaaa'),
      }).setOrigin(0.5);

      cell.add([img, label]);
      container.add(cell);

      x += 130;
      if (x > maxX) {
        x = 60;
        y += cellH;
      }
    });

    return y + cellH;
  }

  private addPlayerAnimationPreview(startY: number): number {
    const container = this.contentContainer!;
    const label = this.add.text(40, startY, 'Animasi Player', {
      ...UIStyle.textStyle('18px', '#d4a373', true),
    });
    container.add(label);

    const sprite = this.add.sprite(120, startY + 70, TEXTURE_KEYS.PLAYER_IDLE_0);
    sprite.setScale(1.2);
    sprite.play(ANIM_KEYS.PLAYER_IDLE);
    container.add(sprite);

    const walkSprite = this.add.sprite(220, startY + 70, TEXTURE_KEYS.PLAYER_WALK_0);
    walkSprite.setScale(1.2);
    walkSprite.play(ANIM_KEYS.PLAYER_WALK);
    container.add(walkSprite);

    const idleLbl = this.add.text(120, startY + 120, 'idle', UIStyle.textStyle('12px', '#888'));
    idleLbl.setOrigin(0.5);
    const walkLbl = this.add.text(220, startY + 120, 'walk', UIStyle.textStyle('12px', '#888'));
    walkLbl.setOrigin(0.5);
    container.add([idleLbl, walkLbl]);

    return startY + 150;
  }

  private addUISample(startY: number): number {
    const container = this.contentContainer!;

    const label = this.add.text(40, startY, 'UI Sample (skala preview)', {
      ...UIStyle.textStyle('18px', '#d4a373', true),
    });
    container.add(label);

    const samples: { key: string; label: string; w: number; h: number }[] = [
      { key: TEXTURE_KEYS.UI_BUTTON, label: 'Button', w: 200, h: 40 },
      { key: TEXTURE_KEYS.UI_DIALOGUE, label: 'Dialogue Box', w: 360, h: 80 },
      { key: TEXTURE_KEYS.UI_QUEST, label: 'Quest Panel', w: 200, h: 80 },
      { key: TEXTURE_KEYS.UI_QUEST, label: 'HUD Panel', w: 200, h: 36 },
    ];

    let x = 60;
    const y = startY + 40;
    samples.forEach((s) => {
      const cell = this.add.container(x, y);
      const img = this.add.image(0, 0, s.key);
      img.setDisplaySize(s.w, s.h);
      const lbl = this.add.text(0, s.h / 2 + 14, s.label, UIStyle.textStyle('12px', '#888'));
      lbl.setOrigin(0.5);
      cell.add([img, lbl]);
      container.add(cell);
      x += s.w + 40;
    });

    const iconCell = this.add.container(x, y);
    const icon = this.add.image(0, 0, TEXTURE_KEYS.ICON_RAFFLESIA);
    icon.setScale(2);
    const iconLbl = this.add.text(0, 30, 'Rafflesia Icon', UIStyle.textStyle('12px', '#888'));
    iconLbl.setOrigin(0.5);
    iconCell.add([icon, iconLbl]);
    container.add(iconCell);

    return startY + 120;
  }
}
